import isNil from 'lodash/isNil';
import {
  useEffect,
  useCallback,
  useState,
} from 'react';

type UseBrowserBackHandlerOptions = {
  /**
   * The state name to use in browser history
   * @example 'mediaDialog', 'appointmentModal', etc.
   */
  stateName: string;
};

/**
 * Custom hook to handle browser back button for dialogs/modals
 *
 * @param options - Configuration options
 * @returns Object containing state and open methods
 *
 * @example
 * ```tsx
 * const { state, open } = useBrowserBackHandler<User>({
 *   stateName: 'userDialog'
 * });
 *
 * // Open dialog with data
 * open({ id: 1, name: 'John' });
 *
 * // Check if open
 * if (state) { ... }
 * ```
 */

export default function useBrowserBackHandler<T = unknown>(
  { stateName }: UseBrowserBackHandlerOptions,
) {
  const [state, setState] = useState<T | null>(null);

  const open = useCallback((data: T | null) => {
    setState(data);

    if (!isNil(data)) {
      // Push state to history when opening
      window.history.pushState({ [stateName]: true }, '');
    } else if (!isNil(window.history.state?.[stateName])) {
      // Remove the state from history if it exists when closing
      window.history.back();
    }
  }, [stateName]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (state && !event.state?.[stateName]) {
        setState(null);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [state, stateName]);

  return {
    state,
    open,
  };
}
